import { useCallback, useMemo, useRef, useState } from "react";

import { HistoryEntityType, type FullAirHandlerUnit } from "@eisox/api/core-api";
import dayjs from "@eisox/dayjs";
import { Loader, Modal, Select } from "@eisox/design-system";
import { CrossIcon, PointChartIcon } from "@eisox/icons";
import { useControllableState } from "@radix-ui/react-use-controllable-state";

import { HISTORY_LOOKBACK_DAYS } from "~/constants/history.constant";
import { ValveHistoryChart } from "~/features/valve";
import { getDateOptions } from "~/helpers/history.helper";
import { useBuildingHistory } from "~/hooks/use-building-history";
import { m } from "~/paraglide/messages";

import type { HistoryPointSelection } from "../helpers/data-selector.helper";
import {
  buildAhuChartMetadata,
  getAhuUnitAxesMapping,
  seriesKey,
  transformAhuHistoryData,
} from "../helpers/history.helper";
import { AirHandlerUnitDataSelector } from "./air-handler-unit-data-selector";
import { DataExportDialog } from "./data-export-dialog";

interface DetailedHistoryDialogProps extends Modal.RootProps {
  buildingId: string;
  airHandlerUnits: FullAirHandlerUnit[];
}

export const DetailedHistoryDialog: React.FC<DetailedHistoryDialogProps> = ({
  buildingId,
  airHandlerUnits,
  children,
  open: openProp,
  onOpenChange,
  defaultOpen = false,
  ...props
}) => {
  const [open, setOpen] = useControllableState({
    prop: openProp,
    onChange: onOpenChange,
    defaultProp: defaultOpen,
  });

  const [selected, setSelected] = useState<HistoryPointSelection[]>([]);
  const [startDate, setStartDate] = useState(dayjs().startOf("day").toISOString());

  const endDate = dayjs(startDate).endOf("day").toISOString();

  const dateOptions = getDateOptions(true, HISTORY_LOOKBACK_DAYS);

  const selectedKey = useMemo(
    () => selected.map(selection => `${selection.entityId}:${selection.point}`).join("|"),
    [selected],
  );

  const { data, isLoading } = useBuildingHistory({
    buildingId,
    startDate,
    endDate,
    entityType: HistoryEntityType.AIR_HANDLER_UNIT,
    series: selected,
    enabled: open && selected.length > 0,
  });

  // Snapshot labels when the selection changes — live AHU polls must not rebuild chart config.
  const metadataRef = useRef<{
    selectedKey: string;
    seriesKeys: string[];
    labels: Record<string, string>;
  }>({
    selectedKey: "",
    seriesKeys: [],
    labels: {},
  });
  if (metadataRef.current.selectedKey !== selectedKey) {
    const metadata = buildAhuChartMetadata(airHandlerUnits, selected);
    metadataRef.current = { selectedKey, seriesKeys: metadata.seriesKeys, labels: metadata.labels };
  }
  const { seriesKeys, labels } = metadataRef.current;

  const unitAxesMapping = useMemo(() => getAhuUnitAxesMapping(selected), [selected]);
  const chartData = useMemo(() => (data ? transformAhuHistoryData(data, selected) : []), [data, selected]);

  const handleRemoveSeries = useCallback((dataKey: string) => {
    setSelected(prev => prev.filter(selection => seriesKey(selection.entityId, selection.point) !== dataKey));
  }, []);

  return (
    <Modal.Root {...props} open={open} onOpenChange={setOpen}>
      <Modal.Trigger asChild>{children}</Modal.Trigger>
      <Modal.Content className="flex h-[80vh] w-[85vw] flex-col">
        <Modal.Header title={m.detailed_history()} subtitle="" icon={<PointChartIcon />}>
          <Modal.Close asChild>
            <CrossIcon className="cursor-pointer" />
          </Modal.Close>
        </Modal.Header>
        <div className="gap-x-2.5 flex">
          <AirHandlerUnitDataSelector airHandlerUnits={airHandlerUnits} selected={selected} onChange={setSelected} />
          <Select.Root multiple={false} value={startDate} onValueChange={setStartDate}>
            <Select.Trigger>
              <Select.Value />
            </Select.Trigger>
            <Select.Content>
              <Select.Viewport>
                {dateOptions.map(option => (
                  <Select.Item key={option.value} value={option.value}>
                    {option.name}
                  </Select.Item>
                ))}
              </Select.Viewport>
            </Select.Content>
          </Select.Root>
          <DataExportDialog
            buildingId={buildingId}
            airHandlerUnits={airHandlerUnits}
            data={selected}
            date={startDate}
          />
        </div>
        <div className="min-h-0 mt-10 flex flex-[1_1_auto] items-center justify-center">
          {isLoading ? (
            <Loader size={24} />
          ) : (
            <ValveHistoryChart
              data={chartData}
              points={seriesKeys}
              unitAxesMapping={unitAxesMapping}
              labels={labels}
              startDate={startDate}
              onRemoveSeries={handleRemoveSeries}
            />
          )}
        </div>
      </Modal.Content>
    </Modal.Root>
  );
};

export type { DetailedHistoryDialogProps };
